import { useRecoilValue } from 'recoil';
import {
    hasUnsavedChangesState,
    patientRepositoryState,
} from '~/state/atoms/patientAtoms';
import AppConfig from '~/utils/appconfig';

/** Incoming data that belongs to a patient other than the loaded one(s). */
export type PatientConflict = {
    loadedPatientIds: string[];
    /** Incoming PatientIDs that no loaded patient carries. */
    incomingPatientIds: string[];
    hasUnsavedChanges: boolean;
    /** Clinical mode never offers loading both patients. */
    clinicalUse: boolean;
};

export type PatientGuardResult =
    | { action: 'allow' }
    | ({ action: 'conflict' } & PatientConflict);

/**
 * Hook that provides a guard function to check whether incoming DICOM data
 * belongs to a different patient than the one currently loaded.
 *
 * A conflict is always raised when the incoming data belongs to a different
 * patient; how the user may resolve it is up to the caller.
 */
export default function usePatientGuard() {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);

    function checkPatientConflict(
        incomingPatientIds: string[]
    ): PatientGuardResult {
        const loadedPatientIds = patientRepository
            .getAll()
            .map((p) => p.dataSet.PatientID);
        if (loadedPatientIds.length === 0) {
            return { action: 'allow' };
        }

        const conflictingIds = incomingPatientIds.filter(
            (id) => !loadedPatientIds.includes(id)
        );

        if (conflictingIds.length === 0) {
            return { action: 'allow' };
        }

        return {
            action: 'conflict',
            loadedPatientIds,
            incomingPatientIds: conflictingIds,
            hasUnsavedChanges,
            clinicalUse: AppConfig.clinicalUse,
        };
    }

    return { checkPatientConflict };
}
